import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import { Component, type ErrorInfo, type ReactNode } from "react"

interface ErrorBoundaryProps {
  /**
   * Subtree to protect. A render error thrown anywhere inside is caught and replaced with the
   * fallback alert.
   */
  children: ReactNode

  /**
   * Human-readable name of the protected area, used in the fallback message (e.g. "Data Quality
   * Rules"). Keep it short - it is rendered inline in a sentence.
   */
  section: string

  /**
   * Changing this value discards a previous error and re-renders `children`. Pass the identity of
   * whatever the subtree renders (e.g. the active tab id) so navigating away and back gives the
   * subtree a fresh attempt instead of showing a stale error.
   */
  resetKey?: string
}

interface ErrorBoundaryState {
  error: Error | null
  prevResetKey: string | undefined
}

/**
 * Catches render errors in a subtree and shows an inline Cloudscape alert instead of unmounting the
 * whole React tree.
 *
 * Without a boundary, one malformed field anywhere in a page takes down the entire view with a
 * white screen (see #605: a `primaryKeyFields` value stored as a string instead of an array threw
 * `value.filter is not a function` and blanked the DTA editor). Wrapping a bounded area keeps the
 * rest of the page - navigation especially - usable.
 *
 * Error boundaries must be class components; React has no hook equivalent.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, prevResetKey: this.props.resetKey }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  // Derive from props (not setState in componentDidUpdate) to reset on resetKey change without an extra render.
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey !== state.prevResetKey) {
      return { error: null, prevResetKey: props.resetKey }
    }
    return null
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[ErrorBoundary] ${this.props.section} failed to render:`, error, errorInfo)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <Box padding="l">
        <Alert type="error" header={`${this.props.section} could not be displayed`}>
          Something went wrong while rendering this section, so it has been hidden to keep the rest
          of the page working. Your unsaved changes elsewhere are unaffected. Try reloading the
          page; if the problem persists, this record's data may need to be corrected.
          <Box variant="small" padding={{ top: "s" }}>
            {error.message}
          </Box>
        </Alert>
      </Box>
    )
  }
}

export default ErrorBoundary
